import React, { useId } from "react";
import PropTypes from "prop-types";

const TLLogoLoader = ({
  label = "Loading...",
  size = 64,
  align = "center",
  layout = "stack",
  className = "",
  style,
}) => {
  const rawId = useId();
  const safeId = rawId.replace(/:/g, "");
  const gradientId = `tl-logo-gradient-${safeId}`;
  const width = size;
  const height = Math.round((size * 4) / 7);
  const ariaLabel = label || "Loading";

  return (
    <div
      className={`tl-logo-loader${layout === "inline" ? " tl-logo-loader--inline" : ""}${
        align === "start" ? " tl-logo-loader--start" : ""
      }${className ? ` ${className}` : ""}`}
      style={style}
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
    >
      <div className="tl-logo-loader__stage" style={{ width, height }}>
        <div className="tl-logo-loader__logo">
          <svg
            className="tl-logo-loader__svg"
            viewBox="0 0 7 4"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#00ffd5" />
                <stop offset="100%" stopColor="#008c7a" />
              </linearGradient>
            </defs>
            <g fill={`url(#${gradientId})`}>
              <path d="M 0 0 V 0.5 H 1 V 2 H 1.5 V 0 Z" />
              <path d="M 3 0 V 2 H 4.5 V 1.5 H 3.5 V 0 H 3 Z" />
              <rect className="tl-logo-loader__square" x="2" y="0" width="0.5" height="0.5" rx="0.06" />
            </g>
          </svg>
        </div>
      </div>
      {label && <div className="tl-logo-loader__label">{label}</div>}
      <style>{`
        .tl-logo-loader {
          display: inline-flex;
          flex-direction: column;
          align-items: center;
          gap: 0.45rem;
          color: #64748b;
        }

        .tl-logo-loader--start {
          align-items: flex-start;
        }

        .tl-logo-loader--inline {
          flex-direction: row;
          gap: 0.4rem;
        }

        .tl-logo-loader__stage {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          perspective: 600px;
        }

        .tl-logo-loader__logo {
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
          animation: tl-logo-flip 1.8s infinite ease-in-out;
        }

        .tl-logo-loader__svg {
          width: 100%;
          height: 100%;
          display: block;
          filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.18));
        }

        .tl-logo-loader__square {
          transform-box: fill-box;
          transform-origin: center;
          animation: tl-logo-square-drop 1.8s infinite ease-in-out;
        }

        .tl-logo-loader__label {
          font-size: 0.85rem;
          line-height: 1.2;
          color: #64748b;
        }

        @keyframes tl-logo-square-drop {
          0% { transform: translateY(0); }
          45% { transform: translateY(300%); }
          60% { transform: translateY(300%); }
          100% { transform: translateY(0); }
        }

        @keyframes tl-logo-flip {
          0%, 60% { transform: rotateY(0deg); }
          80% { transform: rotateY(180deg); }
          100% { transform: rotateY(360deg); }
        }

        @media (prefers-reduced-motion: reduce) {
          .tl-logo-loader__logo,
          .tl-logo-loader__square {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
};

TLLogoLoader.propTypes = {
  label: PropTypes.string,
  size: PropTypes.number,
  align: PropTypes.oneOf(["center", "start"]),
  layout: PropTypes.oneOf(["stack", "inline"]),
  className: PropTypes.string,
  style: PropTypes.object,
};

export default TLLogoLoader;
